/**
 * aegisLocalSeedAnswer.js — AEGIS-PANEL-SEED-RESPONDER-1
 *
 * Local, data-grounded fallback for the AEGIS panel when the governed model
 * backend is not configured in this environment (governedAegisCall → mocked).
 * Instead of a dead "(local stub)" non-answer, summarize the operator's REAL
 * seeded operations data (the same DataService reads the Command Center uses)
 * so the panel is useful in demo/preview.
 *
 * HONESTY (this is a governance surface, not a chatbot): the result is rendered
 * as an explicitly-labeled LOCAL DATA SUMMARY — never as a governed model answer.
 * It carries NO fabricated model/confidence/Decision-Twin; `sources` are the
 * real record ids the summary is built from (genuine provenance). If nothing
 * matches or there's no data, returns null (caller keeps the honest "model not
 * configured" message).
 */
import DataService from '../services/DataService.js';

const LABEL = 'Local summary from your operations data (the AEGIS model isn’t configured in this environment, so this is a direct read of your seeded records — not a governed model answer):';

const hasAny = (re, q) => re.test(q);

export async function aegisLocalSeedAnswer(query, tenantId) {
  if (!tenantId) return null;
  const q = String(query || '').toLowerCase();
  try {
    // ── At-risk shipments / loads ──────────────────────────────────────────
    if (hasAny(/\b(at[- ]?risk|risk|load|shipment|truck|carrier|late)\b/, q)) {
      const rows = await DataService.getAtRiskShipments(tenantId);
      if (rows?.length) {
        const lines = rows.map((r) =>
          `• ${r.loadId} — ${r.origin} → ${r.destination} (${(r.status || '').replace(/_/g, ' ')})${r.delta ? ` · ${r.delta}` : ''}`);
        return {
          content: `${LABEL}\n\nYou have ${rows.length} at-risk shipment${rows.length === 1 ? '' : 's'}:\n${lines.join('\n')}`,
          sources: rows.map((r) => r.loadId).filter(Boolean),
        };
      }
    }
    // ── Pending approvals ──────────────────────────────────────────────────
    if (hasAny(/\b(approv|ratif|pending|decision|tender)\b/, q)) {
      const rows = await DataService.getPendingApprovals(tenantId);
      if (rows?.length) {
        const lines = rows.map((r) =>
          `• ${r.title} (${r.entityId}) — ${r.agentName ?? r.requester}${typeof r.confidence === 'number' ? ` · confidence ${r.confidence.toFixed(2)}` : ''}`);
        return {
          content: `${LABEL}\n\n${rows.length} approval${rows.length === 1 ? '' : 's'} awaiting ratification:\n${lines.join('\n')}`,
          sources: rows.map((r) => r.entityId).filter(Boolean),
        };
      }
    }
    // ── Open exceptions ────────────────────────────────────────────────────
    if (hasAny(/\b(exception|detention|excursion|pod|missing|severit)\b/, q)) {
      const rows = await DataService.getOpenExceptions(tenantId);
      if (rows?.length) {
        const lines = rows.map((r) =>
          `• ${(r.type || '').replace(/_/g, ' ')} (${r.entityId}) — ${r.severity}`);
        return {
          content: `${LABEL}\n\n${rows.length} open exception${rows.length === 1 ? '' : 's'}:\n${lines.join('\n')}`,
          sources: rows.map((r) => r.entityId).filter(Boolean),
        };
      }
    }
    // ── AEGIS handled today ────────────────────────────────────────────────
    if (hasAny(/\b(handled|did|done|autonom|today|action)\b/, q)) {
      const rows = await DataService.getAegisActionsToday(tenantId);
      if (rows?.length) {
        const lines = rows.map((r) =>
          `• ${(r.actionType || '').replace(/_/g, ' ')}${r.entityId ? ` (${r.entityId})` : ''} — ${r.outcome}`);
        return {
          content: `${LABEL}\n\nAEGIS completed ${rows.length} autonomous action${rows.length === 1 ? '' : 's'} today:\n${lines.join('\n')}`,
          sources: rows.map((r) => r.entityId || r.id).filter(Boolean),
        };
      }
    }
    // ── Operations brief / general "what's going on" ───────────────────────
    const brief = await DataService.getOperationsBrief(tenantId);
    if (brief && (brief.narrativeText || (Array.isArray(brief.lines) && brief.lines.length))) {
      const text = brief.narrativeText || brief.lines.join(' ');
      const refs = Array.isArray(brief.sourceRefs) ? brief.sourceRefs.map((s) => s.id).filter(Boolean) : [];
      return { content: `${LABEL}\n\nToday’s operations brief:\n${text}`, sources: refs };
    }
    // ── Fallback: honest snapshot of the counts ────────────────────────────
    const [atRisk, approvals, exceptions] = await Promise.all([
      DataService.getAtRiskShipments(tenantId),
      DataService.getPendingApprovals(tenantId),
      DataService.getOpenExceptions(tenantId),
    ]);
    const total = (atRisk?.length || 0) + (approvals?.length || 0) + (exceptions?.length || 0);
    if (total > 0) {
      return {
        content: `${LABEL}\n\nRight now: ${atRisk?.length || 0} at-risk shipment(s), ${approvals?.length || 0} pending approval(s), ${exceptions?.length || 0} open exception(s). Ask about any of those for detail.`,
        sources: [],
      };
    }
    return null;
  } catch {
    return null;
  }
}

export default aegisLocalSeedAnswer;
